import { Link, useOutletContext } from 'react-router-dom';
import { goalStatusLabel } from '@/domain/workflow/documents';
import { PATTERN_LABELS } from '@/domain/workflow/longitudinal';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import type { ClientOutlet } from './ClientShell';

const GOAL_LEVEL: Record<string, number> = {
  not_started: 0,
  exploring: 1,
  progressing: 2,
  maintaining: 3,
  achieved: 4,
  paused: -1,
  terminated: -1,
};

/** 흐름 (M7): 회기 흐름 · 목표 추이 · 반복 패턴 · 사례개념화 이력 바로가기 */
export function TimelineScreen() {
  const { client } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const { data } = useLoad(() => svc.longitudinal(client.id), [svc, client.id]);
  if (!data) return <p className="muted">불러오는 중…</p>;
  const { flow, trends, patterns, formulationVersions } = data;

  return (
    <div className="stack">
      <div className="card">
        <h2>회기 흐름</h2>
        {flow.length === 0 ? (
          <p className="muted">아직 회기가 없습니다.</p>
        ) : (
          <div className="table-scroll">
            <table className="form-table flow-table">
              <thead>
                <tr>
                  <th>회기</th>
                  <th>주제 · 요약</th>
                  <th>위험</th>
                  <th>개입</th>
                  <th>변화</th>
                </tr>
              </thead>
              <tbody>
                {flow.map((r) => (
                  <tr key={r.sessionId}>
                    <td>
                      <Link to={`/sessions/${r.sessionId}`}>
                        <strong>{r.sessionNumber}회기</strong>
                      </Link>
                      <div className="muted small">
                        {r.date}
                        {r.minutes ? ` · ${r.minutes}분` : ''}
                      </div>
                      {!r.approved && <span className="badge badge-review">진행 중</span>}
                    </td>
                    <td>
                      {r.theme && <div>{r.theme}</div>}
                      {r.summary && r.summary !== r.theme && <div className="muted small">{r.summary}</div>}
                    </td>
                    <td>
                      {r.risks.length ? (
                        r.risks.map((x) => (
                          <span key={x} className="badge badge-review">
                            {x}
                          </span>
                        ))
                      ) : (
                        <span className="muted">-</span>
                      )}
                    </td>
                    <td className="small">{r.interventions.join(', ') || '-'}</td>
                    <td className="small">{r.changes.join(' / ') || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>목표 진행 추이</h2>
        {trends.length === 0 ? (
          <p className="muted">
            목표가 없습니다. <Link to={`/clients/${client.id}/goals`}>목표 추가</Link>
          </p>
        ) : (
          <div className="table-scroll">
            <table className="form-table trend-table">
              <thead>
                <tr>
                  <th>목표</th>
                  {trends[0]!.cells.map((c) => (
                    <th key={c.sessionNumber}>{c.sessionNumber}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trends.map((t) => (
                  <tr key={t.goal.id}>
                    <th>
                      {t.goal.title}
                      <div className="muted small">현재 {goalStatusLabel(t.goal.status)}</div>
                    </th>
                    {t.cells.map((c) => (
                      <td
                        key={c.sessionNumber}
                        className={`trend-cell lv${c.status ? GOAL_LEVEL[c.status] : 'x'} ${c.proposed ? 'proposed' : ''}`}
                        title={
                          c.status ? `${goalStatusLabel(c.status)}${c.proposed ? ' (승인 전 제안)' : ''}` : '기록 없음'
                        }
                      >
                        {c.status ? goalStatusLabel(c.status) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">점선 칸은 승인 전 제안입니다. 회기 화면에서 목표 진행을 승인하면 확정됩니다.</p>
      </div>

      <div className="card">
        <h2>반복되는 패턴</h2>
        {patterns.length === 0 ? (
          <p className="muted">두 회기 이상에서 비슷하게 나온 정서·인지·행동·관계 항목이 아직 없습니다.</p>
        ) : (
          <ul className="list">
            {patterns.map((p, k) => (
              <li key={k} className="list-row">
                <span>
                  <span className="badge">{PATTERN_LABELS[p.category]}</span> {p.label}
                  {p.hypothesis && <span className="muted small"> (가설 포함)</span>}
                  {p.variants.length > 1 && (
                    <div className="muted small">
                      비슷한 표현: {p.variants.filter((v) => v !== p.label).join(' / ')}
                    </div>
                  )}
                </span>
                <span className="small">{p.sessionNumbers.map((n) => `${n}회기`).join(', ')}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="muted small">회기 분석에 기록된 항목을 글자 유사도로 묶은 것입니다. 해석은 상담자가 합니다.</p>
      </div>

      <div className="card">
        <h2>사례개념화 개정 이력</h2>
        <p className="muted small">
          승인된 버전 {formulationVersions}개. 버전별 비교, 가설이 추가·강화·약화·폐기된 기록은 사례개념화 탭에
          있습니다.
        </p>
        <Link className="button" to={`/clients/${client.id}/formulation`}>
          사례개념화 이력 보기
        </Link>
      </div>
    </div>
  );
}
